import { faChevronRight, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components";

import ToDoSubtaskCheckBox from "../../ToDo/ToDoList/components/ToDoSubtaskCheckBox";
import { ToDoStatus } from "../enum/ToDoStatus.enum";
import { SubTaskInfo, TaskInfo } from "../schema/tasks-and-subtasks";
import { canMoveTo, statusDotClass } from "../statusStyles";
import CreateSubTaskInput from "./CreateSubTaskInput";

interface SubTaskListProps {
	task: TaskInfo;
	subTasks: SubTaskInfo[];
	onOpenSubTask: (subTaskId: number) => void;
}

/**
 * Subtasks as rows you can actually work with: tick one off without opening it,
 * and see how far the task is from finished.
 *
 * The checkbox is `ToDoSubtaskCheckBox` — it drives the same socket event as the
 * board and had no call sites until now. It is disabled where the board would
 * refuse the drag too (Done is only reachable from QA).
 */
export default function SubTaskList({ task, subTasks, onOpenSubTask }: SubTaskListProps) {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) => t(`to-do.${key}`, options ?? {});

	const [isCreating, setIsCreating] = useState(false);
	const inputRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (isCreating) {
			inputRef.current?.focus();
		}
	}, [isCreating]);

	const doneCount = useMemo(
		() => subTasks.filter((subTask) => subTask.status === ToDoStatus.DONE).length,
		[subTasks]
	);

	const progress = subTasks.length ? (doneCount / subTasks.length) * 100 : 0;

	return (
		<div className="w-full flex flex-col gap-3">
			{subTasks.length > 0 ? (
				<>
					<div className="flex items-center justify-between gap-2">
						<h3 className="text-[16px] font-bold leading-5 text-primary-blue-dark">
							{ts("subTasks")}
						</h3>
						<span className="text-[12px] leading-4 text-subtle">
							{ts("subtaskProgress", { done: doneCount, total: subTasks.length })}
						</span>
					</div>

					<div className="w-full h-[6px] bg-primary-gray-lightest rounded-full">
						<div
							className="h-full bg-progress-bar rounded-full transition-all"
							style={{ width: `${progress}%` }}
						/>
					</div>

					<ul className="w-full flex flex-col gap-2">
						{subTasks.map((subTask) => {
							const status = subTask?.status ?? ToDoStatus.TO_DO;
							const isDone = status === ToDoStatus.DONE;
							const isCheckBlocked = !isDone && !canMoveTo(status, ToDoStatus.DONE);

							return (
								<li
									key={subTask?.id}
									className="flex items-center gap-3 rounded-[12px] border border-border px-3 py-2.5 transition-colors hover:bg-surface"
								>
									<ToDoSubtaskCheckBox
										className="!mt-0"
										data={task}
										disabled={isCheckBlocked}
										disabledReason={ts("cannot-move-subtask-to-done")}
										subTask={subTask}
										subTaskNameShowing={false}
									/>
									<button
										className="flex flex-1 items-center gap-3 text-left min-w-0"
										type="button"
										onClick={() => {
											if (subTask.id) {
												onOpenSubTask(subTask.id);
											}
										}}
									>
										<span
											className={`flex-1 text-[14px] font-semibold leading-5 text-primary-blue-dark ${
												isDone ? "line-through text-subtle" : ""
											}`}
										>
											{subTask?.title}
										</span>
										<span className="shrink-0 flex items-center gap-1.5 text-[12px] leading-4 text-subtle whitespace-nowrap">
											<span className={`w-2 h-2 rounded-full ${statusDotClass[status]}`} />
											{ts(`${status}`)}
										</span>
										<Icon className="w-3 h-3 shrink-0 text-subtle" icon={faChevronRight} />
									</button>
								</li>
							);
						})}
					</ul>
				</>
			) : null}

			{isCreating ? (
				<CreateSubTaskInput
					inputRef={inputRef}
					taskData={task}
					taskId={task.id ?? -1}
					onClose={() => setIsCreating(false)}
				/>
			) : (
				<button
					className="w-full flex items-center gap-2 rounded-[12px] border border-dashed border-border px-3 py-2.5 text-left text-subtle transition-colors hover:bg-surface hover:text-primary-blue-dark"
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						setIsCreating(true);
					}}
				>
					<Icon className="w-3 h-3 shrink-0" icon={faPlus} />
					<span className="text-[14px] font-semibold leading-5">
						{t("createTask.createSubtask")}
					</span>
				</button>
			)}
		</div>
	);
}
